import { Session } from "@iventis/calendar/components/types";
import { Group } from "@iventis/types";

/**
 * Removes all groups without any assoicated sessions via internal recursive function.
 * @param groups The groups to check for usage in the provided items
 * @param items The items which reference groups
 * @param itemGroupIdGetter Get the ID of the group referenced by an item
 * @returns The groups which are used in the provided items
 */
export const removeUnusedGroups = <T = Session>(groups: Group[], items: T[], itemGroupIdGetter: (item: T) => string) => {
    const removeUnusedGroupsRecursively = (group: Group): Group => {
        if (isGroupUsedInSessions(group.groupId, items, itemGroupIdGetter)) {
            return group;
        }
        if (isGroupOrChildrenUsedInSessions(group, items, itemGroupIdGetter)) {
            const filteredSubGroups: Group[] = [];
            group.subGroups.forEach((value) => {
                if (removeUnusedGroupsRecursively(value) !== undefined) {
                    filteredSubGroups.push(removeUnusedGroupsRecursively(value));
                }
            });
            return { ...group, subGroups: filteredSubGroups };
        }
        return undefined;
    };

    const filteredGroups: Group[] = [] as Group[];

    // Filters valid groups
    if (items.length) {
        groups.forEach((group) => {
            const filteredGroup = removeUnusedGroupsRecursively(group);
            if (filteredGroup !== undefined) {
                filteredGroups.push(filteredGroup);
            }
        });
    }

    return filteredGroups;
};

const isGroupUsedInSessions = <T>(groupID: string, items: T[], itemGroupIdGetter: (item: T) => string) => items.some((session) => itemGroupIdGetter(session) === groupID);

const isGroupOrChildrenUsedInSessions = <T>(group: Group, items: T[], itemGroupIdGetter: (item: T) => string): boolean =>
    isGroupUsedInSessions(group.groupId, items, itemGroupIdGetter) ||
    (group.subGroups !== undefined ? group.subGroups.some((subGroup) => isGroupOrChildrenUsedInSessions(subGroup, items, itemGroupIdGetter)) : false);
